import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const CATEGORIES = [
  { value: 'vegetarian', label: 'Végétarien' },
  { value: 'meat', label: 'Viande' },
  { value: 'fish', label: 'Poisson' },
  { value: 'dessert', label: 'Desserts' },
  { value: 'menus_prestige', label: 'Menus Prestige' },
  { value: 'apero_prestige', label: 'Apéro Prestige' },
  { value: 'apero_dinatoire', label: 'Apéro Dînatoire' },
  { value: 'desserts_prestige', label: 'Desserts Haute Pâtisserie' },
];

const TEMP_TYPES = [
  { value: '', label: '— Aucun —' },
  { value: 'cold', label: 'Froid' },
  { value: 'hot', label: 'Chaud' },
];

const MOIS_OPTIONS = [
  { value: '', label: '— Aucun —' },
  { value: 'mois_1', label: 'Mois I' },
  { value: 'mois_2', label: 'Mois II' },
  { value: 'mois_3', label: 'Mois III' },
];

const emptyDish = {
  name: '',
  description: '',
  price: 0,
  category: 'vegetarian',
  image_url: '',
  ingredients: [],
  is_available: true,
  is_featured: false,
  menu_type: '',
  temp_type: '',
  formule_type: '',
  mois: '',
  mois_label: '',
};

export const AdminDishesPage = () => {
  const { axiosInstance } = useAuth();
  const [dishes, setDishes] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [form, setForm] = useState(emptyDish);
  const [ingredientsStr, setIngredientsStr] = useState('');

  const fetchDishes = useCallback(async () => {
    const res = await axiosInstance.get('/admin/dishes');
    setDishes(res.data);
  }, [axiosInstance]);

  useEffect(() => { fetchDishes(); }, [fetchDishes]);

  const openCreate = () => {
    setEditingDish(null);
    setForm(emptyDish);
    setIngredientsStr('');
    setShowForm(true);
  };

  const openEdit = (dish) => {
    setEditingDish(dish);
    setForm({
      ...emptyDish,
      ...dish,
      menu_type: dish.menu_type || '',
      temp_type: dish.temp_type || '',
      formule_type: dish.formule_type || '',
      mois: dish.mois || '',
      mois_label: dish.mois_label || '',
    });
    setIngredientsStr((dish.ingredients || []).join(', '));
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      ingredients: ingredientsStr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      menu_type: form.menu_type || null,
      temp_type: form.temp_type || null,
      formule_type: form.formule_type || null,
      mois: form.mois || null,
      mois_label: form.mois_label || null,
    };
    try {
      if (editingDish) {
        await axiosInstance.put(`/dishes/${editingDish.dish_id}`, payload);
        toast.success('Plat mis à jour');
      } else {
        await axiosInstance.post('/dishes', payload);
        toast.success('Plat créé');
      }
      setShowForm(false);
      fetchDishes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (dish) => {
    if (!window.confirm(`Supprimer "${dish.name}" ?`)) return;
    try {
      await axiosInstance.delete(`/dishes/${dish.dish_id}`);
      toast.success('Plat supprimé');
      fetchDishes();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filtered = filter === 'all' ? dishes : dishes.filter((d) => d.category === filter);

  return (
    <div data-testid="admin-dishes">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#3E2723]">Gestion des plats</h1>
          <p className="text-[#6D4C41] mt-1">{dishes.length} plat(s) au catalogue</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D4A373] hover:bg-[#c18e5e] text-white rounded-lg font-medium transition-colors"
          data-testid="admin-create-dish"
        >
          <Plus size={18} /> Nouveau plat
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === 'all' ? 'bg-[#3E2723] text-white' : 'bg-white text-[#3E2723] border border-[#D4C4AA]/60'}`}
        >Tous</button>
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            onClick={() => setFilter(c.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === c.value ? 'bg-[#3E2723] text-white' : 'bg-white text-[#3E2723] border border-[#D4C4AA]/60'}`}
          >{c.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#D4C4AA]/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5EFE3] text-[#6D4C41]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Nom</th>
              <th className="text-left px-4 py-3 font-medium">Catégorie</th>
              <th className="text-left px-4 py-3 font-medium">Prix</th>
              <th className="text-left px-4 py-3 font-medium">Dispo</th>
              <th className="text-left px-4 py-3 font-medium">Spécial</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((dish) => (
              <tr key={dish.dish_id} className="border-t border-[#F5EFE3]" data-testid={`admin-dish-row-${dish.dish_id}`}>
                <td className="px-4 py-3 text-[#3E2723] font-medium">{dish.name}</td>
                <td className="px-4 py-3 text-[#6D4C41]">{CATEGORIES.find((c) => c.value === dish.category)?.label || dish.category}</td>
                <td className="px-4 py-3 text-[#3E2723]">{dish.price.toFixed(2)} CHF</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${dish.is_available ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                    {dish.is_available ? 'Oui' : 'Non'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-[#8B7355]">
                  {dish.mois_label || dish.temp_type || dish.menu_type || dish.formule_type || '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(dish)} className="p-1.5 hover:bg-[#F5EFE3] rounded text-[#3E2723]" data-testid={`admin-edit-${dish.dish_id}`}>
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => handleDelete(dish)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600 ml-1" data-testid={`admin-delete-${dish.dish_id}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6D4C41]/60">Aucun plat</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={handleSave} className="bg-white rounded-2xl p-6 w-full max-w-2xl my-8" data-testid="admin-dish-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-bold text-[#3E2723]">{editingDish ? 'Modifier le plat' : 'Nouveau plat'}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-[#F5EFE3] rounded"><X size={20} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Nom</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field w-full" data-testid="dish-name" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Description</label>
                <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field w-full" data-testid="dish-description" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Prix (CHF)</label>
                <input type="number" step="0.50" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-field w-full" data-testid="dish-price" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Catégorie</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-field w-full" data-testid="dish-category">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">URL de l'image</label>
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="input-field w-full" placeholder="https://..." />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Ingrédients (séparés par des virgules)</label>
                <input value={ingredientsStr} onChange={(e) => setIngredientsStr(e.target.value)} className="input-field w-full" placeholder="ex: tomate, basilic, mozzarella" />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Type température</label>
                <select value={form.temp_type} onChange={(e) => setForm({ ...form, temp_type: e.target.value })} className="input-field w-full">
                  {TEMP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Menu type</label>
                <input value={form.menu_type} onChange={(e) => setForm({ ...form, menu_type: e.target.value })} className="input-field w-full" placeholder="prestige / dinatoire" />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Mois (desserts)</label>
                <select value={form.mois} onChange={(e) => setForm({ ...form, mois: e.target.value })} className="input-field w-full">
                  {MOIS_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6D4C41] mb-1">Libellé du mois</label>
                <input value={form.mois_label} onChange={(e) => setForm({ ...form, mois_label: e.target.value })} className="input-field w-full" placeholder="ex: Harmonies Printanières" />
              </div>

              <div className="flex items-center gap-4 md:col-span-2">
                <label className="flex items-center gap-2 text-sm text-[#3E2723]">
                  <input type="checkbox" checked={form.is_available} onChange={(e) => setForm({ ...form, is_available: e.target.checked })} />
                  Disponible
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3E2723]">
                  <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                  Coup de cœur
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-[#6D4C41] hover:bg-[#F5EFE3] rounded-lg">Annuler</button>
              <button type="submit" className="px-5 py-2 bg-[#3E2723] hover:bg-[#5d3b34] text-white rounded-lg font-medium" data-testid="dish-save">
                {editingDish ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
